
import React from 'react';
import { Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import TimeRangeSelector from './usage/TimeRangeSelector';
import UsageChartLegend from './usage/UsageChartLegend';
import UsageAreaChart from './usage/UsageAreaChart';
import { useTimeRange } from '@/hooks/useTimeRange';
import { useHiddenSeries } from '@/hooks/useHiddenSeries';

type UtilityType = 'energy' | 'gas' | 'water';

interface UsageChartProps {
  className?: string;
  utilityType: UtilityType;
}

// Sample data for each utility type
const energyData = [
  { name: 'Jan', electricity: 250 },
  { name: 'Feb', electricity: 260 },
  { name: 'Mar', electricity: 270 },
  { name: 'Apr', electricity: 280 },
  { name: 'May', electricity: 300 },
  { name: 'Jun', electricity: 320 },
  { name: 'Jul', electricity: 350 },
  { name: 'Aug', electricity: 330 },
  { name: 'Sep', electricity: 320 },
  { name: 'Oct', electricity: 290 },
  { name: 'Nov', electricity: 300 },
  { name: 'Dec', electricity: 320 },
];

const gasData = [
  { name: 'Jan', gas: 120 },
  { name: 'Feb', gas: 130 },
  { name: 'Mar', gas: 110 },
  { name: 'Apr', gas: 90 },
  { name: 'May', gas: 80 },
  { name: 'Jun', gas: 70 },
  { name: 'Jul', gas: 60 },
  { name: 'Aug', gas: 65 },
  { name: 'Sep', gas: 75 },
  { name: 'Oct', gas: 90 },
  { name: 'Nov', gas: 110 },
  { name: 'Dec', gas: 120 },
];

const waterData = [
  { name: 'Jan', water: 80 },
  { name: 'Feb', water: 85 },
  { name: 'Mar', water: 90 },
  { name: 'Apr', water: 95 },
  { name: 'May', water: 100 },
  { name: 'Jun', water: 110 },
  { name: 'Jul', water: 120 },
  { name: 'Aug', water: 115 },
  { name: 'Sep', water: 110 },
  { name: 'Oct', water: 100 },
  { name: 'Nov', water: 95 },
  { name: 'Dec', water: 90 },
];

const getUtilityData = (utilityType: UtilityType) => {
  switch (utilityType) {
    case 'energy':
      return energyData;
    case 'gas':
      return gasData;
    case 'water':
      return waterData;
  }
};

const UsageChart: React.FC<UsageChartProps> = ({ className, utilityType }) => {
  const { timeRange, setTimeRange, getFilteredData } = useTimeRange('year');
  const { hiddenSeries, toggleSeriesVisibility } = useHiddenSeries();
  
  const utilityData = getUtilityData(utilityType);
  
  // Type-safe filtered data handling
  const filteredData = (() => {
    const filter = (data: any[]) => getFilteredData(
      data, 
      timeRange === 'month' ? 1 : timeRange === 'quarter' ? 3 : 12
    );
    
    return filter(utilityData);
  })();

  const getChartTitle = () => {
    switch (utilityType) {
      case 'energy':
        return 'Energy Consumption Trends';
      case 'gas':
        return 'Gas Consumption Trends';
      case 'water':
        return 'Water Consumption Trends';
    }
  };

  return (
    <Card className={cn("overflow-hidden bg-[#0A0F1A] border-gray-800", className)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-gray-800">
        <CardTitle className="text-lg font-semibold text-white">{getChartTitle()}</CardTitle>
        <TimeRangeSelector 
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
        />
      </CardHeader>
      <CardContent className="p-0 pt-4">
        <div className="h-80 w-full px-2">
          <UsageAreaChart 
            data={filteredData || []}
            hiddenSeries={hiddenSeries}
            utilityType={utilityType}
          />
        </div>
        <Legend 
          content={({ payload }) => (
            <UsageChartLegend 
              payload={payload || []} 
              hiddenSeries={hiddenSeries}
              onToggleVisibility={toggleSeriesVisibility}
              utilityType={utilityType}
            />
          )}
          verticalAlign="top" 
          height={36}
        />
      </CardContent>
    </Card>
  );
};

export default UsageChart;
